/**
 * ManageInstrumentsDialog.js
 *
 * Multi-page dialog for managing which standardised rating scales are
 * assigned to newly-created patients and informants.  UI structure is
 * aligned with ManageOrgConfigDialog and ManageNotificationsDialog:
 *
 *   1. **Menu view** - compact action rows listing the three groups
 *      (Patient Rating Scales, Informant Adulthood, Informant Childhood)
 *      inside an "Actions" card.
 *
 *   2. **Detail view** - toggle table with read-only / editing flow:
 *        - Patient scales: boolean toggles with AQ-10/AQ-50 mutual
 *          exclusivity.
 *        - Informant instruments: single-select toggles (one must
 *          remain active per life stage).
 *        - "Make Changes" → edit → "Save Changes" or "Cancel".
 *
 * Changes only affect *newly-created* patients/informants; existing
 * users keep the instruments they were originally assigned.
 *
 * Props:
 *   open             - boolean controlling dialog visibility
 *   onOpenChange     - callback for Radix dialog open state
 *   instrumentConfig - module-keyed instruments config from Firestore
 *                      e.g. { adultAdhd: {...}, adultAutism: {...} }
 *   isSaving         - boolean, true while a save is in flight
 *   onSave           - async callback receiving the full module-keyed config
 *   orgName          - organisation name, used to filter org-restricted scales
 *   availableModules - array of module keys the org has enabled (e.g. ['adultAdhd','adultAutism'])
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import {
  INSTRUMENT_GROUPS,
  PATIENT_SCALE_OPTIONS,
  MODULE_SCALE_SETS,
  MODULE_INSTRUMENT_GROUPS,
  ASSESSMENT_MODULE_LABELS,
} from '../config/instrumentSettingsConfig';
import InfoTooltip from '../../../../components/InfoTooltip';
import ConfigHistoryTimeline from './ConfigHistoryTimeline';

// ─── Icons (compact - w-4 h-4 for menu rows, w-5 h-5 for nav) ──────────

const ChevronRightIcon = () => (
  <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const CloseIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

// ─── Group icons (w-4 h-4 to match org config / notifications dialogs) ──

const groupIcons = {
  cypInformantScales: (
    <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
    </svg>
  ),
  patientScales: (
    <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  informantAdulthood: (
    <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  informantChildhood: (
    <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
};

// Short action labels for the compact menu rows
const groupActionLabels = {
  patientScales: 'Edit Patient Rating Scales',
  informantAdulthood: 'Edit Adulthood Informant Rating Scale',
  informantChildhood: 'Edit Childhood Informant Rating Scale',
  // View, not Edit: SNAP-IV 26 cannot be switched off.
  cypInformantScales: 'View Informant Rating Scale',
};

// Sections passed to the history CF for this dialog's audit trail
const HISTORY_SECTIONS = ['instruments'];


export default function ManageInstrumentsDialog({
  open,
  onOpenChange,
  instrumentConfig,
  isSaving,
  onSave,
  orgName = '',
  availableModules = ['adultAdhd'],
}) {
  // ── Module tab state (for multi-pathway orgs) ────────────────────────
  const isMultiModule = availableModules.length > 1;
  const [activeModule, setActiveModule] = useState(availableModules[0] || 'adultAdhd');

  // ── Navigation state ─────────────────────────────────────────────────
  const [activeGroup, setActiveGroup] = useState(null);

  // ── Editing state ────────────────────────────────────────────────────
  // workingConfig holds the FULL module-keyed object during edit so that
  // switching tabs preserves unsaved changes across modules.
  const [isEditing, setIsEditing] = useState(false);
  const [workingConfig, setWorkingConfig] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState('');
  const [saveError, setSaveError] = useState('');

  // ── Animated height transition ────────────────────────────────────────
  const contentRef = useRef(null);
  const [animatedHeight, setAnimatedHeight] = useState('auto');
  const currentView = activeGroup ? (isEditing ? 'edit' : 'detail') : 'menu';

  const measureAndAnimate = useCallback(() => {
    if (!contentRef.current) return;
    setAnimatedHeight(contentRef.current.scrollHeight + 'px');
  }, []);

  // Reset when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setActiveGroup(null);
      setActiveModule(availableModules[0] || 'adultAdhd');
      setIsEditing(false);
      setWorkingConfig(null);
      setSaveSuccess('');
      setSaveError('');
      setAnimatedHeight('auto');
    }
  }, [open, availableModules]);

  // Re-measure on view changes
  useEffect(() => {
    const timer = setTimeout(measureAndAnimate, 30);
    return () => clearTimeout(timer);
  }, [open, currentView, activeGroup, activeModule, saveSuccess, saveError, measureAndAnimate]);

  // ── Derived: current module's config ───────────────────────────────────
  // instrumentConfig is module-keyed: { adultAdhd: {...}, adultAutism: {...} }
  // We read from the active module's sub-object for display purposes.
  const currentModuleConfig = useMemo(() => {
    if (isEditing && workingConfig) {
      return workingConfig[activeModule] || {};
    }
    return instrumentConfig?.[activeModule] || {};
  }, [instrumentConfig, workingConfig, isEditing, activeModule]);

  // ── Helpers ──────────────────────────────────────────────────────────

  // Which instrument groups to show for the current module tab
  const visibleGroups = useMemo(() => {
    const allowedKeys = MODULE_INSTRUMENT_GROUPS[activeModule] || ['patientScales'];
    return INSTRUMENT_GROUPS.filter((g) => allowedKeys.includes(g.groupKey));
  }, [activeModule]);

  // Patient scales filtered by module availability AND org restrictions
  const visibleScales = useMemo(() => {
    const allowedKeys = MODULE_SCALE_SETS[activeModule] || [];
    return PATIENT_SCALE_OPTIONS.filter((item) => {
      if (!allowedKeys.includes(item.key)) return false;
      if (item.restrictToOrgs && !item.restrictToOrgs.includes(orgName)) return false;
      return true;
    });
  }, [activeModule, orgName]);

  const getVisibleItems = useCallback((group) => {
    if (!group) return [];
    if (group.groupKey === 'patientScales') return visibleScales;
    return group.items;
  }, [visibleScales]);

  const isItemEnabled = useCallback((item, group, config) => {
    // alwaysOn scales are part of the pathway for every organisation.
    // A stored false must not paint the switch as off.
    if (item.alwaysOn) return true;
    if (group.firestoreKey) {
      return config?.[group.firestoreKey] === item.key;
    }
    return config?.patientScales?.[item.key] ?? false;
  }, []);

  const getGroupSummary = useCallback((group) => {
    if (group.readOnly) {
      const count = (group.items || []).filter((item) =>
        isItemEnabled(item, group, currentModuleConfig)
      ).length;
      return `${count} enabled`;
    }
    if (group.firestoreKey) {
      const selectedKey = currentModuleConfig?.[group.firestoreKey] || '';
      return selectedKey ? '1 enabled' : 'Not set';
    }
    const scales = currentModuleConfig?.patientScales || {};
    const count = visibleScales.filter((i) => scales[i.key]).length;
    return `${count} enabled`;
  }, [currentModuleConfig, visibleScales, isItemEnabled]);

  // True when the working copy differs from the original config
  const hasChanges = isEditing && workingConfig
    ? JSON.stringify(workingConfig) !== JSON.stringify(instrumentConfig)
    : false;

  const enterEditMode = () => {
    setWorkingConfig(JSON.parse(JSON.stringify(instrumentConfig)));
    setIsEditing(true);
    setSaveSuccess('');
    setSaveError('');
  };

  const cancelEdit = () => {
    setWorkingConfig(null);
    setIsEditing(false);
  };

  /**
   * Unified toggle handler — operates on the active module's sub-config
   * within the full workingConfig object.
   */
  const handleToggle = (itemKey, group) => {
    if (!isEditing || !workingConfig) return;

    const toggledItem = (group.items || []).find((item) => item.key === itemKey)
      || PATIENT_SCALE_OPTIONS.find((item) => item.key === itemKey);
    // Always-on scales are not an org choice. Ignore the click so a save
    // cannot record them as off.
    if (toggledItem?.alwaysOn || group.readOnly) return;

    if (group.firestoreKey) {
      const currentlySelected = workingConfig[activeModule]?.[group.firestoreKey];
      if (currentlySelected === itemKey) return;
      setWorkingConfig((prev) => ({
        ...prev,
        [activeModule]: {
          ...prev[activeModule],
          [group.firestoreKey]: itemKey,
        },
      }));
    } else {
      const allItems = [...PATIENT_SCALE_OPTIONS, ...group.items];
      const scaleMeta = allItems.find((s) => s.key === itemKey);

      setWorkingConfig((prev) => {
        const updated = JSON.parse(JSON.stringify(prev));
        if (!updated[activeModule]) updated[activeModule] = {};
        if (!updated[activeModule].patientScales) updated[activeModule].patientScales = {};

        const newValue = !updated[activeModule].patientScales[itemKey];
        updated[activeModule].patientScales[itemKey] = newValue;

        if (newValue && scaleMeta?.mutuallyExclusiveWith) {
          updated[activeModule].patientScales[scaleMeta.mutuallyExclusiveWith] = false;
        }

        return updated;
      });
    }
  };

  const handleSave = async () => {
    setSaveError('');
    setSaveSuccess('');
    try {
      await onSave(workingConfig);
      setSaveSuccess('Rating scale settings saved successfully.');
      setIsEditing(false);
      setWorkingConfig(null);
    } catch (err) {
      setSaveError(err?.message || 'Failed to save rating scale settings.');
    }
  };

  const navigateBack = () => {
    if (isEditing) cancelEdit();
    setActiveGroup(null);
    setSaveSuccess('');
    setSaveError('');
  };

  const groupMeta = INSTRUMENT_GROUPS.find((g) => g.groupKey === activeGroup);

  // ── Render: Module tab bar (multi-pathway orgs only) ───────────────

  const renderModuleTabs = () => {
    if (!isMultiModule) return null;
    return (
      <div className="flex space-x-1 bg-gray-100 rounded-lg p-1 mb-3">
        {availableModules.map((mod) => (
          <button
            key={mod}
            type="button"
            onClick={() => setActiveModule(mod)}
            className={`flex-1 px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
              activeModule === mod
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {ASSESSMENT_MODULE_LABELS[mod] || mod}
          </button>
        ))}
      </div>
    );
  };

  // ── Render: Menu view ─────────────────────────────────────────────

  const renderMenu = () => (
    <div className="space-y-3">
      <h3 className="text-lg font-bold text-gray-900 pr-6">
        Rating Scales
      </h3>
      <p className="text-sm text-gray-500">
        Manage which rating scales are assigned to newly-created patients and informants.
        Existing users are not affected by changes here.
      </p>

      {renderModuleTabs()}

      <div className="bg-white border border-gray-200 rounded-md p-3">
        <h4 className="text-xs font-medium text-gray-900 mb-2">Actions</h4>
        <div className="space-y-2">
          {visibleGroups.map((group) => {
            const summary = getGroupSummary(group);
            return (
              <button
                key={group.groupKey}
                type="button"
                onClick={() => {
                  setActiveGroup(group.groupKey);
                  setSaveSuccess('');
                  setSaveError('');
                }}
                className="w-full flex items-center justify-between p-2 text-left border border-gray-200 rounded-md hover:bg-primary/5 hover:border-primary/30 transition-colors"
              >
                <div className="flex items-center space-x-2">
                  {groupIcons[group.groupKey]}
                  <div>
                    <p className="text-xs font-semibold text-gray-900">
                      {groupActionLabels[group.groupKey]}
                    </p>
                    <p className="text-xs text-gray-500">
                      {group.subtitle}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 flex-shrink-0">
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 whitespace-nowrap">
                    {summary}
                  </span>
                  <ChevronRightIcon />
                </div>
              </button>
            );
          })}

          {/* View History action - shows the audit trail timeline */}
          <button
            type="button"
            onClick={() => {
              setActiveGroup('history');
              setSaveSuccess('');
              setSaveError('');
            }}
            className="w-full flex items-center justify-between p-2 text-left border border-gray-200 rounded-md hover:bg-primary/5 hover:border-primary/30 transition-colors"
          >
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-xs font-semibold text-gray-900">View History</p>
                <p className="text-xs text-gray-500">See recent rating scale configuration changes</p>
              </div>
            </div>
            <ChevronRightIcon />
          </button>
        </div>
      </div>
    </div>
  );

  // ── Render: Detail view ───────────────────────────────────────────

  const renderDetail = () => {
    if (!groupMeta) return null;

    return (
      <div className="space-y-3">
        <button
          type="button"
          onClick={navigateBack}
          className="text-xs text-primary hover:text-primary/80 font-semibold"
        >
          &larr; Back to Overview
        </button>

        <h3 className="text-base font-semibold text-gray-900">
          {isEditing ? `Editing: ${groupActionLabels[activeGroup]}` : groupActionLabels[activeGroup]}
        </h3>
        <p className="text-xs text-gray-500">{groupMeta.subtitle}</p>
        {groupMeta.readOnly && (
          <p className="text-xs text-gray-500">
            This scale is assigned to every CYP ADHD case. The parent or carer and the school representative both complete it. It cannot be turned off.
          </p>
        )}

        {/* Feedback banners */}
        {saveSuccess && (
          <div className="p-3 bg-green-100 text-green-700 rounded-lg text-sm">
            {saveSuccess}
          </div>
        )}
        {saveError && (
          <div className="p-3 bg-red-100 text-red-700 rounded-lg text-sm">
            {saveError}
          </div>
        )}

        {/* Table wrapper - keeps header and rows as siblings inside a
            single element so the parent's space-y-3 doesn't inject a gap. */}
        <div>
          {/* Column headers */}
          <div className="grid grid-cols-12 px-3 py-2 bg-gray-100 rounded-t-lg text-xs font-medium text-gray-500 uppercase tracking-wider">
            <div className="col-span-9">Rating Scale</div>
            <div className="col-span-3 text-center">Enabled</div>
          </div>

          {/* Scale rows */}
          <div className="border border-gray-200 border-t-0 rounded-b-lg divide-y divide-gray-100">
          {getVisibleItems(groupMeta).map((item) => {
            const isOn = isItemEnabled(item, groupMeta, currentModuleConfig);
            // alwaysOn stays on in edit mode as well. The pathway assigns
            // the scale to every case, so the switch is never a real choice.
            const isAlwaysOn = item.alwaysOn === true;
            const isDisabled = !isEditing || isSaving || isAlwaysOn || groupMeta.readOnly;

            // For informant groups, the currently-active instrument
            // cannot be turned off - the user must enable the alternative.
            const isLockedOn = isAlwaysOn || (isOn && isEditing && !!groupMeta?.firestoreKey);

            const allItems = groupMeta?.firestoreKey ? groupMeta.items : PATIENT_SCALE_OPTIONS;
            const exclusivePartner = item.mutuallyExclusiveWith
              ? allItems.find((s) => s.key === item.mutuallyExclusiveWith)
              : null;

            let tooltipContent = item.description;
            if (exclusivePartner && groupMeta?.firestoreKey) {
              tooltipContent += `\n\nExactly one must be selected - enabling this will disable ${exclusivePartner.label}.`;
            } else if (exclusivePartner) {
              tooltipContent += `\n\nNote: Cannot be enabled at the same time as ${exclusivePartner.label}.`;
            }

            return (
              <div
                key={item.key}
                className="grid grid-cols-12 items-center px-3 py-3"
              >
                <div className="col-span-9 flex items-center justify-between">
                  <span className="text-sm text-gray-800">{item.label}</span>
                  <InfoTooltip
                    content={tooltipContent}
                    side="right"
                    maxWidth="280px"
                  />
                </div>

                {/* Toggle - full opacity in read-only mode so on/off state
                    is clearly visible; cursor-not-allowed signals editing
                    must be unlocked first. */}
                <div className="col-span-3 flex justify-center">
                  <button
                    type="button"
                    disabled={isDisabled || isLockedOn}
                    aria-pressed={isOn}
                    onClick={() => handleToggle(item.key, groupMeta)}
                    title={
                      isAlwaysOn
                        ? 'Assigned for every organisation. This cannot be turned off.'
                        : isLockedOn
                          ? 'Enable the other option to switch'
                          : (isDisabled ? 'Click "Make Changes" to edit' : undefined)
                    }
                    className={`
                      relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                      focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1
                      ${(isDisabled || isLockedOn)
                        ? (isOn ? 'bg-primary cursor-not-allowed' : 'bg-gray-300 cursor-not-allowed')
                        : isOn
                          ? 'bg-primary cursor-pointer'
                          : 'bg-gray-300 cursor-pointer'
                      }
                    `}
                  >
                    <span
                      className={`
                        inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform
                        ${isOn ? 'translate-x-6' : 'translate-x-1'}
                      `}
                    />
                  </button>
                </div>
              </div>
            );
          })}
          </div>
        </div>

        {/* Action buttons. Read-only groups (CYP SNAP-IV) have nothing to save. */}
        {!groupMeta.readOnly && (
        <div className="flex justify-end space-x-2 pt-2">
          {isEditing ? (
            <>
              <button
                type="button"
                onClick={cancelEdit}
                disabled={isSaving}
                className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving || !hasChanges}
                className={`px-3 py-1.5 text-xs font-semibold text-white rounded-md transition-colors ${
                  isSaving || !hasChanges
                    ? 'bg-gray-400 cursor-not-allowed opacity-50'
                    : 'bg-primary hover:bg-primary/90'
                }`}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={enterEditMode}
              className="px-3 py-1.5 text-xs font-semibold text-white bg-primary rounded-md hover:bg-primary/90 transition-colors inline-flex items-center gap-1.5"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Make Changes
            </button>
          )}
        </div>
        )}
      </div>
    );
  };

  // ── Render ───────────────────────────────────────────────────────────

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />
        <Dialog.Content
          className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-lg z-50 max-w-lg w-[calc(100vw-1rem)] max-h-[95vh] overflow-y-auto"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Dialog.Title className="sr-only">
            Manage Rating Scales
          </Dialog.Title>
          <Dialog.Description className="sr-only">
            Manage which standardised rating scales are assigned to patients and informants.
          </Dialog.Description>

          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="absolute top-2 right-2 p-1 rounded-full text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors z-10"
            aria-label="Close"
          >
            <CloseIcon />
          </button>

          {/* Animated height wrapper for smooth step transitions */}
          <div
            className="overflow-hidden transition-[height] duration-300 ease-in-out"
            style={{ height: animatedHeight }}
          >
            <div ref={contentRef} className="p-4 md:p-6">
              {activeGroup === 'history' ? (
                <ConfigHistoryTimeline
                  sections={HISTORY_SECTIONS}
                  onBack={() => { setActiveGroup(null); }}
                  title="Rating Scales - Change History"
                  onContentChange={measureAndAnimate}
                />
              ) : activeGroup ? renderDetail() : renderMenu()}
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
